/**
 * SmartText.tsx - Intelligent Text Component with Auto Font Detection
 * 
 * Wraps Chakra UI Text component and automatically detects Arabic/Kurdish text
 * to apply the appropriate font (Muna for Arabic/Kurdish, KV-Text for others).
 */

"use client"

import { Text, TextProps } from "@chakra-ui/react"
import { containsArabicOrKurdish } from "@/utilities/detectScript"
import { useMemo, ReactNode, isValidElement } from "react"

interface SmartTextProps extends TextProps {
  children: ReactNode
  text?: string // Optional explicit text prop for detection
}

/**
 * Extracts text content from React children (handles strings, numbers, and nested elements)
 * @param children - React children to extract text from
 * @returns Extracted text content as a string
 */
function extractTextFromChildren(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('')
  }
  if (isValidElement(children)) {
    const props = children.props as { children?: ReactNode }
    if (props?.children) {
      return extractTextFromChildren(props.children)
    }
  }
  return ''
}

/**
 * SmartText - Automatically applies Arabic/Kurdish font when detected
 * 
 * Usage:
 *   <SmartText dir="auto">Hello مرحبا</SmartText>
 *   <SmartText text={someText} dir="auto">{someText}</SmartText>
 *   <SmartText dir="auto">From <em>{title}</em></SmartText>
 */
export const SmartText: React.FC<SmartTextProps> = ({ 
  children, 
  text,
  className,
  dir = "auto",
  ...props 
}) => {
  // Detect Arabic/Kurdish in text
  // Prefer explicit text prop, otherwise extract from children
  const textToCheck = text || extractTextFromChildren(children)
  
  const hasArabicOrKurdish = useMemo(() => {
    return containsArabicOrKurdish(textToCheck)
  }, [textToCheck])

  const fontClassName = hasArabicOrKurdish ? 'arabic-kurdish-font' : undefined
  const combinedClassName = [className, fontClassName].filter(Boolean).join(' ') || undefined

  return (
    <Text 
      dir={dir}
      className={combinedClassName}
      {...props}
    >
      {children}
    </Text>
  )
}

